import {
  AuctionSortFilter,
  CONSTANTS,
  DATE_FORMAT,
  DATE_TYPE,
  NUMBER,
  SIZES,
} from "../misc/constants";
import { useEffect, useState } from "react";
import { useLazyGetAuctionsQuery } from "../store/reducer/auction";
import {
  AuctionStatus,
  AuctionVehicleOrderColumn,
  FRONTEND_ROUTES,
  OrderByDirection,
  VehicleOrderColumn,
} from "../misc/enum";
import { formatDate } from "../misc/utils";
import { ModalSelectOption, SelectOption } from "../ts/component.types";
import { Box, SelectChangeEvent, Toolbar } from "@mui/material";
import SelectComponent from "../components/SelectComponent";
import { appendQueryParams } from "../misc/api.utils";
import { Formik } from "formik";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { useLazyGetAuctionVehiclesQuery } from "../store/reducer/auctionVehicle";
import { DynamicKeyValuesWithNumArray } from "../ts/api.types";
import { acquiredFromOptions } from "../misc/createData";
import { useLazyGetAnnouncementsQuery } from "../store/reducer/setting";
import { useUpdateVehicleMutation } from "../store/reducer/vehicle";
import { useSuccessDispatcher } from "../hooks/useSuccessDispatcher";
import { useErrorDispatcher } from "../hooks/useErrorDispatcher";
import { MESSAGES } from "../misc/messages";
import Loader from "../components/Loader";
import ButtonComponent from "../components/ButtonComponent";

const allAuctionOption = { label: "All", value: NUMBER.MINUS_ONE };
const options: Array<SelectOption> = [allAuctionOption];

const initialValues = {
  auction: NUMBER.MINUS_ONE,
};

const AuctionEdit = () => {
  const successDispatcher = useSuccessDispatcher();
  const errorDispatcher = useErrorDispatcher();

  const [auctionFieldsData, setAuctionFieldsData] =
    useState<Array<SelectOption>>(options);
  const [announcementFieldsData, setAnnouncementFieldsData] =
    useState<Array<SelectOption>>(options);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [auctionVehicleRows, setAuctionVehicleRows] = useState<Array<any>>([]);
  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: NUMBER.ZERO, pageSize: 25 });
  const [count, setCount] = useState<number>(NUMBER.ZERO);
  const [params, setParams] = useState({
    page: paginationModel.page,
    rowsPerPage: paginationModel.pageSize,
    vin: "",
    auctionId: NUMBER.MINUS_ONE?.toString(),
    auctionStatus: NUMBER.MINUS_ONE?.toString(),
    startAge: "",
    endAge: "",
    startYear: "",
    endYear: "",
    sort: AuctionSortFilter.laneRunAscending,
  });

  const [updateVehicle] = useUpdateVehicleMutation();
  const [getAuctions, { data: auctionStateData }] = useLazyGetAuctionsQuery();
  const [getAnnouncements, { data: announcementsData }] =
    useLazyGetAnnouncementsQuery();
  const [getAuctionVehicles, { data: auctionVehicleStateData, isFetching }] =
    useLazyGetAuctionVehiclesQuery();

  useEffect(() => {
    const apiParams = appendQueryParams(
      {
        status: [AuctionStatus.Scheduled, AuctionStatus.Lock],
        isActive: "active",
      },
      false
    );

    getAuctions(apiParams);
    getAnnouncements("is_deleted=false");
  }, [getAuctions]); //eslint-disable-line

  useEffect(() => {
    const data = auctionStateData?.data?.auction;
    if (data) {
      const auctions: any = [allAuctionOption];
      auctionStateData?.data?.auction?.forEach((auction: any) => {
        if (
          auction?.location?.name &&
          (auction?.auction_date || auction?.start_time)
        ) {
          const auctionDate = auction?.start_time
            ? formatDate(
                auction?.start_time,
                DATE_TYPE.TIMESTAMP,
                DATE_FORMAT.YYYY_MM_DD
              )
            : formatDate(
                auction.auction_date,
                DATE_TYPE.DATE_STRING,
                DATE_FORMAT.YYYY_MM_DD
              );
          auctions.push({
            label: auctionDate + " : " + auction?.location?.name,
            value: auction.id,
          });
        }
      });

      setAuctionFieldsData(auctions);
    }
  }, [auctionStateData]); //eslint-disable-line

  useEffect(() => {
    const announcements = announcementsData?.data?.announcements;
    if (announcements) {
      const announcementFields = parseData(announcements);
      setAnnouncementFieldsData(announcementFields);
    }
  }, [announcementsData]); //eslint-disable-line

  useEffect(() => {
    (async () => {
      await fetchData(params);
    })();
  }, [params]); //eslint-disable-line

  const updateAuctionId = (auctionId: string) => {
    setIsLoading(true);
    setParams({
      ...params,
      auctionId,
    });
  };

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
    setIsLoading(true);
    setParams({
      ...params,
      page: newModel.page,
      rowsPerPage: newModel.pageSize,
    });
  };

  const parseData = (data: Array<ModalSelectOption>) => {
    const mappedOptions = data?.map((row) => {
      return {
        label: row?.name || row?.label,
        value: row.id,
      };
    });

    return mappedOptions;
  };

  const fetchData = async (queryParams: DynamicKeyValuesWithNumArray) => {
    const auctionId = queryParams.auctionId;
    const status = queryParams.auctionStatus;

    let apiParams = ``;

    const queryParamObj: DynamicKeyValuesWithNumArray = {
      page: (queryParams.page as number) + 1,
      limit: queryParams.rowsPerPage as number,
      startYear: queryParams?.startYear as string,
      endYear: queryParams?.endYear as string,
      startAge: queryParams?.startAge as string,
      endAge: queryParams?.endAge as string,
      vin: queryParams?.vin as string,
      lane: queryParams?.lane as number,
      run: queryParams?.run as number,
    };

    queryParamObj["direction"] = OrderByDirection.ASC;
    queryParamObj["column"] = AuctionVehicleOrderColumn.LaneRun;

    if (auctionId && Number(auctionId) >= NUMBER.ZERO)
      queryParamObj["auctionId"] = auctionId;
    if (status && Number(status) >= NUMBER.ZERO)
      queryParamObj["status"] = status;

    apiParams += appendQueryParams(queryParamObj, false);

    try {
      const response: any = await getAuctionVehicles(apiParams);
      let vehiclesData = response?.data?.data?.auction_vehicles || [];

      const formatted = formatAuctionVehicle(
        vehiclesData,
        (+queryParamObj.page - 1) * +queryParamObj.limit
      );

      setCount(response?.data?.data?.count);
      setIsLoading(false);
      setAuctionVehicleRows(formatted);
    } catch (err) {
      setAuctionVehicleRows([]);
    }
  };

  const allColumnProps: GridColDef = {
    field: "",
    headerAlign: "left",
    align: "left",
    type: "string",
  };
  const unEditableColumnProps: GridColDef = {
    ...allColumnProps,
    cellClassName: "uneditable-class",
  };
  const editableColumnProps: GridColDef = {
    ...allColumnProps,
    cellClassName: "editable-class",
  };
  const columns: GridColDef[] = [
    {
      ...unEditableColumnProps,
      field: "index",
      headerName: "No#",
      width: 75,
    },
    { ...unEditableColumnProps, field: "vin", headerName: "VIN", width: 200 },
    {
      ...editableColumnProps,
      field: "acquired_from",
      headerName: "Acquired From",
      width: 150,
      type: "singleSelect",
      valueOptions: [...acquiredFromOptions],
      editable: true,
    },
    { ...unEditableColumnProps, field: "year", headerName: "Year" },
    { ...unEditableColumnProps, field: "model", headerName: "Model" },
    { ...unEditableColumnProps, field: "make", headerName: "Make" },
    {
      ...editableColumnProps,
      field: "lane",
      headerName: "Lane",
      width: 75,
      type: "number",
      editable: true,
    },
    {
      ...editableColumnProps,
      field: "run",
      headerName: "Run",
      width: 75,
      type: "number",
      editable: true,
    },
    {
      ...editableColumnProps,
      field: "exterior_color",
      headerName: "Exterior Color",
      width: 150,
      editable: true,
    },
    {
      ...editableColumnProps,
      field: "mileage",
      headerName: "Mileage",
      type: "number",
      editable: true,
    },
    {
      ...editableColumnProps,
      field: "cr_rating",
      headerName: "CR Rating",
      type: "number",
      editable: true,
    },
    {
      ...editableColumnProps,
      field: "floor_price",
      headerName: "Floor Price",
      type: "number",
      editable: true,
    },
    {
      ...editableColumnProps,
      field: "announcement_id",
      headerName: "Announcement",
      width: 150,
      type: "singleSelect",
      valueOptions: [...announcementFieldsData],
      editable: true,
    },
    {
      ...unEditableColumnProps,
      width: 300,
      field: "sub_announcements",
      headerName: "Sub Announcements",
    },
  ];

  const checkUpdates = (newRow: any, oldRow: any) => {
    const updates: Record<string, any> = {};

    if (newRow.lane !== oldRow.lane || newRow.run !== oldRow.run) {
      updates["lane"] = newRow.lane;
      updates["run"] = newRow.run;
      updates["auction_vehicle_id"] = newRow.id;
    }
    if (newRow.acquired_from !== oldRow.acquired_from) {
      updates["acquired_from"] = newRow.acquired_from;
    }
    if (newRow.exterior_color !== oldRow.exterior_color) {
      updates["exterior_color"] = newRow.exterior_color;
    }
    if (newRow.mileage !== oldRow.mileage) {
      updates["mileage"] = newRow.mileage;
    }
    if (newRow.cr_rating !== oldRow.cr_rating) {
      updates["cr_rating"] = newRow.cr_rating;
    }
    if (newRow.floor_price !== oldRow.floor_price) {
      updates["floor_price"] = newRow.floor_price;
    }
    if (newRow.announcement_id !== oldRow.announcement_id) {
      updates["announcement_id"] = newRow.announcement_id;
    }

    return updates;
  };

  const validateRow = (updates: any) => {
    let validation = true;
    let message: string[] = [];

    // if lane then it should have run too
    if ("lane" in updates) {
      if (!Number.isInteger(updates.lane) || updates.lane < 0) {
        message.push("lane");
        validation = false;
      }
      if (!Number.isInteger(updates.run) || updates.run < 0) {
        message.push("run");
        validation = false;
      }
    }

    if ("mileage" in updates) {
      if (!Number.isInteger(updates.mileage) || updates.mileage < 0) {
        message.push("mileage");
        validation = false;
      }
    }

    if ("cr_rating" in updates) {
      if (updates.cr_rating < 0) {
        message.push("cr_rating");
        validation = false;
      }
    }

    if ("floor_price" in updates) {
      if (!Number.isInteger(updates.floor_price) || updates.floor_price < 0) {
        message.push("floor_price");
        validation = false;
      }
    }

    return { validation, message: `Invalid ${message.join(", ")}` };
  };

  const handleProcessRowUpdateError = (error: any) => {
    console.error("Row update error:", error);
  };

  const handleProcessRowUpdate = async (newRow: any, oldRow: any) => {
    // check updates
    const updates = checkUpdates(newRow, oldRow);

    if (Object.keys(updates).length > 0) {
      // validate updates
      let { validation, message } = validateRow(updates);
      if (validation) {
        const vehicleUpdateRequest = {
          payload: updates,
          id: newRow?.vehicle_id as string,
        };

        const response: any = await updateVehicle(vehicleUpdateRequest);

        if (response?.data?.message === "Success") {
          successDispatcher(
            true,
            `${MESSAGES.VEHICLE.UPDATE_VEHICLE_SUCCESS} - ${newRow?.vin}`
          );
          setAuctionVehicleRows((prevRows) =>
            prevRows.map((row) => (row.id === newRow.id ? newRow : row))
          );
          return newRow;
        }
        message = response?.error?.error;
      }

      errorDispatcher(true, message);
    }
    return oldRow;
  };

  const handlePrintClick = () => {
    const vehicleIds = auctionVehicleRows.reduce((vehicleList, v) => {
      vehicleList.push(v.vehicle_id);
      return vehicleList;
    }, []);

    const url = `${window.location.origin}${
      FRONTEND_ROUTES.KBB_REPORT
    }/${vehicleIds.join("&")}`;
    window.open(url, "_blank");
  };

  const formatAuctionVehicle = (
    auctionVehicles: any[],
    startingIndex: number
  ) => {
    return auctionVehicles.map((auctionVehicle, i) => {
      return {
        ...auctionVehicle,

        // Uneditable fields of vehicle
        vin: auctionVehicle.vehicle.vin,
        year: auctionVehicle.vehicle.year,
        model: auctionVehicle.vehicle.model,
        make: auctionVehicle.vehicle.make,
        sub_announcements: auctionVehicle.vehicle.remarks
          .map((remark: any) => remark.label)
          .join(", "),
        index: startingIndex + i + 1,

        // Editable fields of vehicle
        acquired_from: auctionVehicle.vehicle.acquired_from,
        exterior_color: auctionVehicle.vehicle.exterior_color,
        mileage: +auctionVehicle.vehicle.mileage,
        cr_rating: +auctionVehicle.vehicle.condition_report.rating,
        floor_price: +auctionVehicle.vehicle.floor_price,
        announcement_id: +auctionVehicle.vehicle.announcement_id, // Convert string announcement_id into int
      };
    });
  };

  return (
    <div className="background-white px-8 py-4">
      <Toolbar className="toolbar flex justify-between items-center">
        <Box className="font-bold text-3xl text-tertiary-200">
          Edit Auction Vehicles
        </Box>
        <Box className="flex gap-4 items-center">
          <Box>
            Select Auction:
            <Formik
              initialValues={initialValues}
              onSubmit={async (
                values: {
                  [key: string]: number | boolean | string | Date;
                },
                { setSubmitting }
              ) => {
                // handleFormSubmit(values);
                setSubmitting(false);
              }}
            >
              {({
                values,
                errors,
                handleChange,
                handleSubmit,
                setFieldValue,
                resetForm,
                /* and other goodies */
              }) => (
                <SelectComponent
                  hideErrorMessage={true}
                  className="w-60"
                  size={SIZES.SMALL}
                  name="auction"
                  value={values["auction"] as string}
                  options={auctionFieldsData}
                  handleChange={(event: SelectChangeEvent<string>) => {
                    const value = event?.target?.value;
                    setFieldValue("auction", value);
                    updateAuctionId(value);
                  }}
                />
              )}
            </Formik>
          </Box>
          <Box>
            <ButtonComponent
              className="margin-4 button-container w-[230px]"
              text="Get Booksheets"
              color={CONSTANTS.PRIMARY}
              size={SIZES.LARGE}
              onClick={handlePrintClick}
            />
          </Box>
        </Box>
      </Toolbar>
      <Box className="mt-12">
        {isLoading ? (
          <Loader />
        ) : auctionVehicleRows.length ? (
          <DataGrid
            rows={auctionVehicleRows}
            columns={columns}
            editMode="row"
            rowCount={count}
            processRowUpdate={handleProcessRowUpdate}
            paginationModel={paginationModel}
            pagination
            paginationMode="server" // Enable server-side pagination
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 25, 100]}
            onProcessRowUpdateError={handleProcessRowUpdateError}
          />
        ) : (
          <Box className="w-full text-center font-bold text-lg text-tertiary-400 tracking-wide">
            No Data Available
          </Box>
        )}
      </Box>
    </div>
  );
};

export default AuctionEdit;
